import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getPlans, createPlan, updatePlan, deletePlan, type Plan } from '@/services'
import { AppButton, EmptyState, PlanBadge, Modal, IconButton, CardGridSkeleton, Skeleton } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { formatCurrency } from '@/lib/utils'

export function PlansPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Plan | null | 'new'>(null)

  const { data: plans = [], isLoading } = useQuery({ queryKey: ['plans'], queryFn: getPlans })
  const highlighted = plans.filter((p) => p.is_highlighted).length

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Billing</div>
          <h1>Planes</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={230} height={14} /> : `${plans.length} planes disponibles · ${highlighted} destacado${highlighted === 1 ? '' : 's'}`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="plus" onClick={() => setEditing('new')}>Nuevo plan</AppButton>
        </div>
      </div>

      {isLoading ? (
        <CardGridSkeleton columns={4} cards={4} minHeight={210} />
      ) : plans.length === 0 ? (
        <EmptyState icon="layers" title="Sin planes" desc="Creá el primer plan de suscripción." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {plans.map((p) => (
            <div key={p.id} className="card" style={p.is_highlighted ? { borderColor: 'var(--bisne-green)', borderWidth: 2, boxShadow: '0 0 0 4px rgba(21,184,104,0.12)' } : undefined}>
              <div style={{ padding: 18, position: 'relative' }}>
                {p.is_highlighted ? <span className="badge badge-success badge-sm" style={{ position: 'absolute', top: 14, right: 14 }}><Icon name="star" size={10} />Destacado</span> : null}
                <div style={{ marginBottom: 10 }}><PlanBadge plan={p.slug} /></div>
                <h2 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 22, margin: 0, letterSpacing: '-0.01em' }}>{p.name}</h2>
                <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 32, marginTop: 8, letterSpacing: '-0.02em' }}>
                  {p.price_monthly > 0 ? formatCurrency(p.price_monthly, p.currency || 'ARS') : 'Gratis'}
                  {p.price_monthly > 0 ? <span className="muted" style={{ fontSize: 13, fontWeight: 500, letterSpacing: 0 }}> / mes</span> : null}
                </div>
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{p.currency || 'ARS'} · {p.trial_days || 0} días de trial</div>
                <div style={{ marginTop: 14, padding: '12px 0', borderTop: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-2)' }}>
                  <div>Hasta <strong>{p.max_branches}</strong> sucursales · <strong>{p.max_users}</strong> usuarios</div>
                  <div style={{ color: 'var(--text-3)', marginTop: 4 }}>{p.max_products?.toLocaleString('es-AR')} productos</div>
                </div>
                <div className="row gap-2" style={{ marginTop: 8 }}>
                  <AppButton variant="secondary" size="sm" icon="edit" onClick={() => setEditing(p)}>Editar</AppButton>
                  <IconButton icon="trash2" tooltip="Eliminar" size={13} onClick={() => { if (window.confirm(`¿Eliminar el plan ${p.name}?`)) deletePlan(p.id).then(() => { toast.success('Plan eliminado'); qc.invalidateQueries({ queryKey: ['plans'] }) }).catch((e) => toast.error(e?.message || 'Error')) }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <PlanFormModal plan={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['plans'] }); setEditing(null) }} />
      ) : null}
    </>
  )
}

function PlanFormModal({ plan, onClose, onSaved }: { plan: Plan | null; onClose: () => void; onSaved: () => void }) {
  const saveMutation = useMutation({
    mutationFn: (body: Partial<Plan>) => plan ? updatePlan(plan.id, body) : createPlan(body),
    onSuccess: () => { toast.success(plan ? 'Plan actualizado' : 'Plan creado'); onSaved() },
    onError: (e: any) => toast.error(e?.message || 'No se pudo guardar el plan'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={plan ? `Editar ${plan.name}` : 'Nuevo plan'}
      footer={
        <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
          <AppButton variant="ghost" onClick={onClose}>Cancelar</AppButton>
          <AppButton variant="primary" icon="check" disabled={saveMutation.isPending} onClick={() => { const f = document.getElementById('plan-form') as HTMLFormElement | null; f?.requestSubmit() }}>{plan ? 'Guardar' : 'Crear plan'}</AppButton>
        </div>
      }
    >
      <form id="plan-form" onSubmit={(e) => {
        e.preventDefault()
        const fd = new FormData(e.currentTarget)
        saveMutation.mutate({
          name: String(fd.get('name') || '').trim(),
          slug: String(fd.get('slug') || '').trim(),
          description: String(fd.get('description') || '') || null,
          price_monthly: Number(fd.get('price_monthly') || 0),
          price_yearly: fd.get('price_yearly') ? Number(fd.get('price_yearly')) : null,
          currency: String(fd.get('currency') || 'ARS'),
          trial_days: Number(fd.get('trial_days') || 0),
          max_branches: Number(fd.get('max_branches') || 0),
          max_users: Number(fd.get('max_users') || 0),
          max_products: Number(fd.get('max_products') || 0),
          is_highlighted: fd.get('is_highlighted') === 'on',
          is_active: fd.get('is_active') === 'on',
        })
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="form-row"><label htmlFor="pl-name">Nombre</label><input id="pl-name" className="input" name="name" defaultValue={plan?.name || ''} required /></div>
          <div className="form-row"><label htmlFor="pl-slug">Slug</label><input id="pl-slug" className="input" name="slug" defaultValue={plan?.slug || ''} required style={{ fontFamily: 'var(--font-mono)' }} /></div>
          <div className="form-row"><label htmlFor="pl-price-monthly">Precio mensual</label><input id="pl-price-monthly" className="input" type="number" name="price_monthly" defaultValue={plan?.price_monthly ?? 0} /></div>
          <div className="form-row"><label htmlFor="pl-price-yearly">Precio anual</label><input id="pl-price-yearly" className="input" type="number" name="price_yearly" defaultValue={plan?.price_yearly ?? ''} /></div>
          <div className="form-row"><label htmlFor="pl-currency">Currency</label><select id="pl-currency" className="select" name="currency" defaultValue={plan?.currency || 'ARS'}><option value="ARS">ARS</option><option value="USD">USD</option></select></div>
          <div className="form-row"><label htmlFor="pl-trial">Días de trial</label><input id="pl-trial" className="input" type="number" name="trial_days" defaultValue={plan?.trial_days ?? 14} /></div>
          <div className="form-row"><label htmlFor="pl-branches">Máx. sucursales</label><input id="pl-branches" className="input" type="number" name="max_branches" defaultValue={plan?.max_branches ?? 1} /></div>
          <div className="form-row"><label htmlFor="pl-users">Máx. usuarios</label><input id="pl-users" className="input" type="number" name="max_users" defaultValue={plan?.max_users ?? 3} /></div>
          <div className="form-row"><label htmlFor="pl-products">Máx. productos</label><input id="pl-products" className="input" type="number" name="max_products" defaultValue={plan?.max_products ?? 100} /></div>
        </div>
        <div className="form-row"><label htmlFor="pl-desc">Descripción</label><textarea id="pl-desc" className="textarea" name="description" defaultValue={plan?.description || ''} /></div>
        <div className="row gap-4">
          <label className="toggle"><input type="checkbox" name="is_highlighted" defaultChecked={plan?.is_highlighted ?? false} />Destacado</label>
          <label className="toggle"><input type="checkbox" name="is_active" defaultChecked={plan ? plan.is_active : true} />Activo</label>
        </div>
      </form>
    </Modal>
  )
}
